import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useGameCtx } from "../../context/GameContext";
import { useAuth } from "../../context/AuthContext";
import { useAvatar } from "../../context/AvatarContext";
import { ensureGuestId, getGuestPseudo } from "../../utils/localStorage";
import { API_BASE_URL } from "../../utils/constants";
import {
  joinGame,
  resolveWebSocketFallbackUrls,
  resolveWebSocketUrl,
} from "../../api/gameService";
import { saveActiveGame, clearActiveGame } from "../../utils/activeGame";

import GameRoomLayout from "./room/GameRoomLayout";
import GameResultOverlay from "./room/GameResultOverlay";
import BugReportWidget from "../../components/ui/BugReportWidget";
import RoomBattleBanner from "./room/RoomBattleBanner";

export default function GameRoom() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedCategory } = useGameCtx();
  const { user, isAuthenticated } = useAuth();
  const { currentAvatar, trackCorrectAnswer, trackFirstPlaceFinish } = useAvatar();

  const navState = location.state || {};
  const category = navState.category || selectedCategory || null;

  const [lastAnswer, setLastAnswer] = useState(null);
  const [answerStatus, setAnswerStatus] = useState(null);
  const [correctStreak, setCorrectStreak] = useState(0);
  const [fastStreak, setFastStreak] = useState(0);
  const [wrongStreak, setWrongStreak] = useState(0);
  const [performanceNotice, setPerformanceNotice] = useState(null);
  const [flawlessBonusUids, setFlawlessBonusUids] = useState(new Set());
  const flawlessBonusTimerRef = useRef(null);

  const wsUrl = navState.wsUrl || null;
  const MEDIA_BASE_URL =
    process.env.REACT_APP_MEDIA_BASE_URL ||
    process.env.VITE_API_URL ||
    API_BASE_URL;

  const [phase, setPhase] = useState("question");
  const [roundIndex, setRoundIndex] = useState(1);
  const [remaining, setRemaining] = useState(20);
  const [totalRounds, setTotalRounds] = useState(15);
  const [gamesCompletedTick, setGamesCompletedTick] = useState(0);
  const [gameBreakSeconds, setGameBreakSeconds] = useState(40);
  const [rounds, setRounds] = useState(() =>
    Array.from({ length: 15 }).map((_, index) => ({
      index: index + 1,
      thumb: null,
    }))
  );
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [currentImageSubmittedBy, setCurrentImageSubmittedBy] = useState(null);
  const [currentImageSourceType, setCurrentImageSourceType] = useState(null);
  const [currentCorrectAnswer, setCurrentCorrectAnswer] = useState(null);
  const [debugAnswer, setDebugAnswer] = useState(null);
  const [players, setPlayers] = useState([]);
  const [gameResults, setGameResults] = useState(null);
  const [showResultOverlay, setShowResultOverlay] = useState(false);
  const [postGameBreakRemaining, setPostGameBreakRemaining] = useState(null);
  const [, setIsTrackingPostGameInterCycle] = useState(false);
  const [isSocketReady, setIsSocketReady] = useState(false);
  const [answerHistory, setAnswerHistory] = useState([]);
  const [answerFeedback, setAnswerFeedback] = useState("");
  const [partialMatchedWords, setPartialMatchedWords] = useState([]);
  const [roundHistory, setRoundHistory] = useState({});
  const [socketFeedback, setSocketFeedback] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [chatFeedback, setChatFeedback] = useState("");

  const wsRef = useRef(null);
  const roundIndexRef = useRef(1);
  const phaseRef = useRef("question");
  const remainingRef = useRef(20);
  const isTrackingPostGameInterCycleRef = useRef(false);
  const pendingAnswerIdsRef = useRef([]);
  const answeredCorrectThisRoundRef = useRef(false);
  const correctStreakRef = useRef(0);
  const fastStreakRef = useRef(0);
  const wrongStreakRef = useRef(0);
  const debugAnswerRoundRef = useRef(null);
  const gameBreakSecondsRef = useRef(40);

  // Refs stables pour les valeurs qui changent avec l'auth mais ne doivent pas
  // provoquer une reconnexion WebSocket.
  const joinPlayerIdRef = useRef(null);
  const joinPseudoRef = useRef(null);
  const joinAvatarIdRef = useRef(null);
  const joinGuestIdRef = useRef(null);
  const currentPlayerUidRef = useRef(null);
  const trackCorrectAnswerRef = useRef(trackCorrectAnswer);
  const trackFirstPlaceFinishRef = useRef(trackFirstPlaceFinish);
  const applyPlayersSnapshotRef = useRef(null);
  const buildPerformanceNoticeRef = useRef(null);

  const localUid = ensureGuestId();
  const authenticatedUserId = user?.user_id || user?.id || null;

  const currentPlayerUid = String(
    authenticatedUserId || navState?.player?.player_id || localUid
  );
  const joinPlayerId = authenticatedUserId || navState?.player?.player_id || localUid;
  const joinPseudo =
    user?.username || navState?.player?.pseudo || getGuestPseudo() || null;
  const joinAvatarId = navState?.player?.avatar_id || currentAvatar?.id || null;
  const joinGuestId = navState?.player?.guest_id || (!user ? localUid : null);

  // Mise à jour des refs à chaque render sans déclencher de reconnexion WS.
  joinPlayerIdRef.current = joinPlayerId;
  joinPseudoRef.current = joinPseudo;
  joinAvatarIdRef.current = joinAvatarId;
  joinGuestIdRef.current = joinGuestId;
  currentPlayerUidRef.current = currentPlayerUid;
  trackCorrectAnswerRef.current = trackCorrectAnswer;
  trackFirstPlaceFinishRef.current = trackFirstPlaceFinish;
  gameBreakSecondsRef.current = gameBreakSeconds;
  const canModeratePlayers = Boolean(
    user?.is_staff || user?.is_superuser || user?.is_moderator
  );
  const confirmLeavingRoom = useCallback(() => {
    return window.confirm("Quitter la partie en cours et ouvrir ce lien ?");
  }, []);

  const handleRoomLinkClick = useCallback(
    (event) => {
      if (event.defaultPrevented) {
        return;
      }

      const target = event.target;
      const anchor =
        target instanceof Element ? target.closest("a[href]") : null;

      if (!anchor) {
        return;
      }

      if (anchor.getAttribute("target") === "_blank") {
        return;
      }

      const rawHref = anchor.getAttribute("href") || "";
      if (
        !rawHref ||
        rawHref.startsWith("#") ||
        rawHref.startsWith("mailto:") ||
        rawHref.startsWith("tel:")
      ) {
        return;
      }

      const destination = new URL(anchor.href, window.location.href);
      const current = new URL(window.location.href);
      const leavesRoom =
        destination.origin !== current.origin ||
        destination.pathname !== current.pathname;

      if (leavesRoom && !confirmLeavingRoom()) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [confirmLeavingRoom]
  );

  const handlePlayAnotherCategory = useCallback(
    async (nextCategory) => {
      if (!nextCategory?.slug) {
        return;
      }

      try {
        const data = await joinGame(nextCategory.slug);

        if (!data?.ws_url) {
          throw new Error("Aucune URL WebSocket recue.");
        }

        const finalWsUrl = resolveWebSocketUrl(data.ws_url);

        setShowResultOverlay(false);
        setGameResults(null);
        setPostGameBreakRemaining(null);
        setIsTrackingPostGameInterCycle(false);
        isTrackingPostGameInterCycleRef.current = false;

        navigate(`/game-quiz/${nextCategory.slug}`, {
          state: {
            wsUrl: finalWsUrl,
            category: nextCategory,
            player: {
              player_id: joinPlayerId,
              pseudo: joinPseudo,
              avatar_id: joinAvatarId,
              guest_id: joinGuestId,
            },
          },
        });
      } catch (error) {
        console.error(error);
        window.alert("Impossible de rejoindre cette categorie pour le moment.");
      }
    },
    [
      joinAvatarId,
      joinGuestId,
      joinPlayerId,
      joinPseudo,
      navigate,
    ]
  );

  const buildPerformanceNotice = useCallback((data, nextStats) => {
    const isFast = Number(data.bonus_fast || 0) > 0;
    const speedRank = Number(data.speed_rank || 0);
    const delta = Number(data.delta || 0);

    if (data.is_correct && isFast) {
      return {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        tone: "fast",
        kicker: `+${delta} pts`,
        title: "Genial, bonne réponse et bonus rapidite !",
        beforeHighlight: "C'est ta ",
        highlight: `${nextStats.fastStreak}e`,
        afterHighlight: " réponse rapide consecutive.",
        detail:
          speedRank > 0
            ? `Top ${speedRank} des joueurs les plus rapides sur cette image.`
            : "Tu fais partie des joueurs les plus rapides.",
      };
    }

    if (data.is_correct) {
      return {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        tone: "correct",
        kicker: `+${delta || 4} pts`,
        title: "Excellente réponse !",
        beforeHighlight: "C'est ta ",
        highlight: `${nextStats.correctStreak}e`,
        afterHighlight: " bonne réponse consecutive.",
        detail: "Garde le rythme pour remonter le classement.",
      };
    }

    return {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      tone: "wrong",
      kicker: `${delta || -1} pt`,
      title: "Malheureusement, ce n'est pas la bonne réponse.",
      beforeHighlight: "C'est ta ",
      highlight: `${nextStats.wrongStreak}e`,
      afterHighlight: " réponse incorrecte consecutive.",
      detail: "Concentre-toi, observe les details et retente vite.",
    };
  }, []);

  const applyPlayersSnapshot = useCallback((snapshot) => {
    setPlayers((previousPlayers) => {
      const safePlayers = Array.isArray(snapshot) ? snapshot : [];

      let normalizedPlayers = safePlayers.map((player) => ({
        uid: player.uid,
        pseudo: player.pseudo || "Joueur",
        score: Number.isFinite(Number(player.score)) ? Number(player.score) : 0,
        online: player.online ?? true,
        status: player.status || "uncounted",
        score_counted: Boolean(player.score_counted),
        avatar_id: player.avatar_id || player.active_avatar || null,
        team: player.team || null,
        is_staff: Boolean(player.is_staff),
        is_moderator: Boolean(player.is_moderator),
        current_speed_rank: player.current_speed_rank ?? null,
        current_correct: Boolean(player.current_correct),
      }));

      const currentUid = String(currentPlayerUid);
      const hasCurrentPlayer = normalizedPlayers.some(
        (player) => String(player.uid) === currentUid
      );

      if (!hasCurrentPlayer) {
        const previousCurrentPlayer = previousPlayers.find(
          (player) => String(player.uid) === currentUid
        );

        if (previousCurrentPlayer && previousCurrentPlayer.online !== false) {
          normalizedPlayers = [...normalizedPlayers, previousCurrentPlayer];
        }
      }

      const sortedPlayers = [...normalizedPlayers].sort(
        (playerA, playerB) => playerB.score - playerA.score
      );

      const previousSortedPlayers = [...previousPlayers].sort(
        (playerA, playerB) => playerB.score - playerA.score
      );
      const oldIndexes = {};
      previousSortedPlayers.forEach((player, index) => {
        oldIndexes[player.uid] = index;
      });

      return sortedPlayers.map((player, index) => {
        const previousIndex = oldIndexes[player.uid];
        let movement = 0;

        if (previousIndex === undefined) {
          movement = 0;
        } else if (previousIndex > index) {
          movement = 1;
        } else if (previousIndex < index) {
          movement = -1;
        }

        return { ...player, movement };
      });
    });
  }, [currentPlayerUid]);

  // Mise à jour des refs des callbacks après leurs déclarations useCallback.
  buildPerformanceNoticeRef.current = buildPerformanceNotice;
  applyPlayersSnapshotRef.current = applyPlayersSnapshot;

  useEffect(() => {
    let socketOpened = false;
    let socketUrlIndex = 0;
    let isCancelled = false;
    let activeWs = null;
    setChatMessages([]);
    setChatFeedback("");
    setSocketFeedback("");

    const websocketUrls = resolveWebSocketFallbackUrls(
      wsUrl || `/ws/game/${slug}/`
    );

    setIsSocketReady(false);

    const connectSocket = () => {
      if (isCancelled || !websocketUrls[socketUrlIndex]) {
        setSocketFeedback("Connexion temps reel impossible. Verifie que Daphne tourne sur 127.0.0.1:8000.");
        return;
      }

      const ws = new WebSocket(websocketUrls[socketUrlIndex]);
      activeWs = ws;
      wsRef.current = ws;

    ws.onopen = () => {
      socketOpened = true;
      setIsSocketReady(true);
      setSocketFeedback("");
      setPlayers((previousPlayers) => {
        const currentUid = String(currentPlayerUidRef.current);
        const playerExists = previousPlayers.some(
          (player) => String(player.uid) === currentUid
        );

        if (playerExists) {
          return previousPlayers.map((player) =>
            String(player.uid) === currentUid
              ? {
                  ...player,
                  pseudo: joinPseudoRef.current || player.pseudo || "Joueur",
                  score: Number.isFinite(Number(player.score))
                    ? Number(player.score)
                    : 0,
                  online: true,
                  avatar_id: player.avatar_id || joinAvatarIdRef.current,
                  team: player.team || null,
                }
              : player
          );
        }

        return [
          ...previousPlayers,
          {
            uid: currentUid,
            pseudo: joinPseudoRef.current || "Joueur",
            score: 0,
            online: true,
            status: "uncounted",
            score_counted: false,
            avatar_id: joinAvatarIdRef.current,
            team: null,
          },
        ];
      });

      saveActiveGame(slug, category?.name || slug);
      ws.send(
        JSON.stringify({
          type: "JOIN_ROOM",
          category: slug,
          player_id: joinPlayerIdRef.current,
          pseudo: joinPseudoRef.current,
          avatar_id: joinAvatarIdRef.current,
          guest_id: joinGuestIdRef.current,
        })
      );
    };

    ws.onmessage = (event) => {
      let data = null;

      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      if (data.type === "ANSWER_RESULT") {
        const pendingEntryId = pendingAnswerIdsRef.current.shift();

        if (data.accepted) {
        if (data.is_partial) {
          setPartialMatchedWords(data.matched_words || []);
          setAnswerHistory((previousHistory) =>
            previousHistory.map((entry) =>
              entry.id === pendingEntryId
                ? { ...entry, text: data.raw_answer || entry.text, status: "partial" }
                : entry
            )
          );
        } else {
          const isFast = Boolean(data.is_correct && Number(data.bonus_fast || 0) > 0);
          const nextCorrectStreak = data.is_correct
            ? correctStreakRef.current + 1
            : 0;
          const nextFastStreak = isFast ? fastStreakRef.current + 1 : 0;
          const nextWrongStreak = data.is_correct
            ? 0
            : wrongStreakRef.current + 1;
          const nextStats = {
            correctStreak: nextCorrectStreak,
            fastStreak: nextFastStreak,
            wrongStreak: nextWrongStreak,
          };

          correctStreakRef.current = nextCorrectStreak;
          fastStreakRef.current = nextFastStreak;
          wrongStreakRef.current = nextWrongStreak;
          setCorrectStreak(nextCorrectStreak);
          setFastStreak(nextFastStreak);
          setWrongStreak(nextWrongStreak);
          setPerformanceNotice(buildPerformanceNoticeRef.current(data, nextStats));
          setAnswerFeedback(
            data.is_correct
              ? isFast
                ? "Bonne réponse + bonus rapidite."
                : "Bonne réponse."
              : "Mauvaise réponse, retente."
          );
          setLastAnswer(data.raw_answer || "");
          setAnswerStatus(data.is_correct ? "correct" : "wrong");
          if (data.is_correct) setPartialMatchedWords([]);
          answeredCorrectThisRoundRef.current = Boolean(data.is_correct);
          setRoundHistory((prev) => {
            const idx = roundIndexRef.current;
            const existing = prev[idx] || {};
            if (existing.isCorrect) return prev;
            return {
              ...prev,
              [idx]: {
                ...existing,
                isCorrect: Boolean(data.is_correct),
                speedRank: data.is_correct ? (Number(data.speed_rank) || null) : null,
              },
            };
          });
          setAnswerHistory((previousHistory) =>
            previousHistory.map((entry) =>
              entry.id === pendingEntryId
                ? {
                    ...entry,
                    text: data.raw_answer || entry.text,
                    status: data.is_correct ? "correct" : "wrong",
                    bonusFast: Number(data.bonus_fast || 0) > 0,
                    speedRank: data.speed_rank || null,
                    delta: data.delta,
                  }
                : entry
            )
          );

          if (data.is_correct) {
            trackCorrectAnswerRef.current();
          }

          if (typeof data.current_score === "number") {
            setPlayers((previousPlayers) =>
              previousPlayers.map((player) =>
                String(player.uid) === String(currentPlayerUid)
                  ? { ...player, score: data.current_score }
                  : player
              )
            );
          }
        } // fin else (non partiel)
        } else if (pendingEntryId) {
          const reasonMessages = {
            empty: "Réponse vide.",
            not_in_question_phase: "La question n'est pas ouverte.",
            no_correct_answer: "Aucune réponse officielle pour cette image.",
            unknown_player: "Joueur non reconnu dans la room.",
            already_correct: "Tu as deja trouve la bonne réponse.",
          };
          const message =
            reasonMessages[data.reason] || "Réponse refusee par le serveur.";
          setAnswerFeedback(message);
          if (data.reason === "already_correct") {
            setPerformanceNotice({
              id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              tone: "locked",
              kicker: "Deja valide",
              title: "Tu as deja trouve cette image.",
              beforeHighlight: "Attends la ",
              highlight: "prochaine",
              afterHighlight: " image pour scorer encore.",
              detail: "Le score est verrouille apres une bonne réponse.",
            });
          }
          setAnswerHistory((previousHistory) =>
            previousHistory.map((entry) =>
              entry.id === pendingEntryId
                ? {
                    ...entry,
                    status: "rejected",
                    message,
                  }
                : entry
            )
          );
        }

        return;
      }

      if (data.type === "ROUND_UPDATE") {
        const nextRoundIndex = Number(data.index || 1);
        const nextPhase = data.phase || "question";
        const isNewQuestionRound =
          nextPhase === "question" &&
          (nextRoundIndex !== roundIndexRef.current ||
            phaseRef.current !== "question");

        if (isNewQuestionRound) {
          if (!answeredCorrectThisRoundRef.current) {
            setCorrectStreak(0);
            setFastStreak(0);
            correctStreakRef.current = 0;
            fastStreakRef.current = 0;
          }
          answeredCorrectThisRoundRef.current = false;
          setLastAnswer(null);
          setAnswerStatus(null);
          setAnswerFeedback("");
          setPerformanceNotice(null);
          setAnswerHistory([]);
          setPartialMatchedWords([]);
          pendingAnswerIdsRef.current = [];
          if (nextRoundIndex === 1) {
            setRoundHistory({});
          }
        }

        setRoundIndex(data.index);
        setPhase(data.phase);
        setRemaining(data.remaining);
        if (Number.isFinite(Number(data.total_rounds)) && Number(data.total_rounds) > 0) {
          setTotalRounds(Number(data.total_rounds));
        }
        if (
          Number.isFinite(Number(data.game_break_seconds)) &&
          Number(data.game_break_seconds) > 0
        ) {
          setGameBreakSeconds(Number(data.game_break_seconds));
        }
        roundIndexRef.current = nextRoundIndex;
        phaseRef.current = nextPhase;
        remainingRef.current = Number(data.remaining || 0);

        if (isTrackingPostGameInterCycleRef.current) {
          if (nextPhase === "break") {
            setPostGameBreakRemaining(Number(data.remaining || 0));
          } else if (nextPhase === "question" && nextRoundIndex === 1) {
            setPostGameBreakRemaining(null);
            setIsTrackingPostGameInterCycle(false);
            isTrackingPostGameInterCycleRef.current = false;
            setShowResultOverlay(false);
            setGameResults(null);
          }
        }

        if (data.image_path) {
          setCurrentImageUrl(`${MEDIA_BASE_URL}${data.image_path}`);
          setCurrentImageSubmittedBy(data.submitted_by || null);
          setCurrentImageSourceType(data.source_type || null);
        }

        setCurrentCorrectAnswer(data.answer || null);

        if (nextPhase === "break" && data.answer) {
          setRoundHistory((prev) => ({
            ...prev,
            [nextRoundIndex]: {
              ...(prev[nextRoundIndex] || {}),
              answer: data.answer,
            },
          }));
        }

        if (Object.prototype.hasOwnProperty.call(data, "debug_answer") && data.debug_answer) {
          debugAnswerRoundRef.current = nextRoundIndex;
          setDebugAnswer(data.debug_answer);
        } else if (debugAnswerRoundRef.current !== nextRoundIndex) {
          debugAnswerRoundRef.current = null;
          setDebugAnswer(null);
        }

        if (Array.isArray(data.thumbs)) {
          const nextRounds = data.thumbs.map((thumb) => ({
            index: thumb.index,
            thumb: thumb.image_path
              ? `${MEDIA_BASE_URL}${thumb.image_path}`
              : null,
          }));
          setRounds(nextRounds);
          if (nextRounds.length > 0) {
            setTotalRounds(nextRounds.length);
          }
        }

        return;
      }

      if (data.type === "GAME_FINISHED") {
        clearActiveGame();
        setGamesCompletedTick((tick) => tick + 1);
        const overallResults = data.overall_results || [];
        const bonusUids = new Set(
          overallResults
            .filter((r) => Number(r.flawless_bonus || 0) > 0)
            .map((r) => String(r.uid || r.player_id || ""))
        );
        if (bonusUids.size > 0) {
          setFlawlessBonusUids(bonusUids);
          if (flawlessBonusTimerRef.current) clearTimeout(flawlessBonusTimerRef.current);
          flawlessBonusTimerRef.current = setTimeout(() => setFlawlessBonusUids(new Set()), 5000);
        }
        const myResult = overallResults.find(
          (result) =>
            String(result.uid || result.player_id || "") === currentPlayerUid
        );
        const topScore = overallResults.reduce(
          (maxScore, result) => Math.max(maxScore, Number(result.score || 0)),
          0
        );

        if (myResult && Number(myResult.score || 0) === topScore && topScore > 0) {
          trackFirstPlaceFinishRef.current();
        }

        setGameResults({
          overall_results: data.overall_results || [],
          counted_results: data.counted_results || data.results || [],
          uncounted_results: data.uncounted_results || [],
          image_sources: Array.isArray(data.image_sources)
            ? data.image_sources
            : [],
        });
        setIsTrackingPostGameInterCycle(true);
        isTrackingPostGameInterCycleRef.current = true;
        setPostGameBreakRemaining(
          Number.isFinite(Number(data.break_remaining))
            ? Number(data.break_remaining)
            : phaseRef.current === "break"
            ? remainingRef.current
            : gameBreakSecondsRef.current
        );
        if (Number.isFinite(Number(data.total_rounds)) && Number(data.total_rounds) > 0) {
          setTotalRounds(Number(data.total_rounds));
        }
        if (
          Number.isFinite(Number(data.game_break_seconds)) &&
          Number(data.game_break_seconds) > 0
        ) {
          setGameBreakSeconds(Number(data.game_break_seconds));
        }
        setShowResultOverlay(true);
        return;
      }

      if (data.type === "PLAYERS_SNAPSHOT") {
        applyPlayersSnapshotRef.current(data.players || []);
        return;
      }

      if (data.type === "JOIN_BLOCKED") {
        setSocketFeedback(
          data.message || "Acces a la partie bloque par une sanction active."
        );
        return;
      }

      if (data.type === "CHAT_SNAPSHOT") {
        setChatMessages(Array.isArray(data.messages) ? data.messages : []);
        return;
      }

      if (data.type === "CHAT_MESSAGE") {
        if (data.message) {
          setChatMessages((previousMessages) => [
            ...previousMessages,
            data.message,
          ].slice(-25));
          setChatFeedback("");
        }
        return;
      }

      if (data.type === "CHAT_BLOCKED") {
        setChatFeedback(data.message || "Message bloque.");
        return;
      }

      if (data.type === "PLAYER_JOINED") {
        const joinedUid = String(data.uid);
        setPlayers((previousPlayers) => {
          const playerExists = previousPlayers.some(
            (player) => String(player.uid) === joinedUid
          );

          if (playerExists) {
            return previousPlayers.map((player) =>
              String(player.uid) === joinedUid
                ? {
                    ...player,
                    pseudo: data.pseudo || player.pseudo,
                    score:
                      typeof data.score === "number" ? data.score : player.score,
                    status: data.status || player.status || "uncounted",
                    score_counted:
                      typeof data.score_counted === "boolean"
                        ? data.score_counted
                        : Boolean(player.score_counted),
                    online: data.online ?? true,
                    avatar_id: data.avatar_id || player.avatar_id || null,
                    team: data.team || player.team || null,
                  }
                : player
            );
          }

          return [
            ...previousPlayers,
            {
              uid: joinedUid,
              pseudo: data.pseudo || "Joueur",
              score: typeof data.score === "number" ? data.score : 0,
              online: data.online ?? true,
              status: data.status || "uncounted",
              score_counted: Boolean(data.score_counted),
              avatar_id: data.avatar_id || null,
              team: data.team || null,
            },
          ];
        });

        if (joinedUid !== String(currentPlayerUid)) {
          setChatMessages((prev) => [
            ...prev,
            {
              id: `system-join-${joinedUid}-${Date.now()}`,
              type: "system",
              text: `${data.pseudo || "Joueur"} est entré dans la salle`,
            },
          ].slice(-25));
        }
        return;
      }

      if (data.type === "PLAYER_LEFT") {
        setPlayers((previousPlayers) =>
          previousPlayers.map((player) =>
            player.uid === data.uid ? { ...player, online: false } : player
          )
        );

        if (data.pseudo) {
          setChatMessages((prev) => [
            ...prev,
            {
              id: `system-left-${data.uid}-${Date.now()}`,
              type: "system",
              text: `${data.pseudo} a quitté la salle`,
            },
          ].slice(-25));
        }
      }
    };

    ws.onerror = () => {
      if (!socketOpened) {
        setSocketFeedback("Connexion temps reel en cours...");
      }
    };

    ws.onclose = () => {
      setIsSocketReady(false);
      if (wsRef.current === ws) {
        wsRef.current = null;
      }

      if (!socketOpened && !isCancelled) {
        socketUrlIndex += 1;
        connectSocket();
        return;
      }

      if (!isCancelled) {
        setSocketFeedback("Connexion temps reel interrompue. Recharge la room si besoin.");
      }
    };
    };

    connectSocket();

    return () => {
      isCancelled = true;
      setIsSocketReady(false);
      activeWs?.close();
    };
  // Seules les dépendances qui doivent vraiment provoquer une reconnexion WS.
  // Les valeurs liées à l'auth (joinPlayerId, joinPseudo, etc.) et les
  // callbacks (trackCorrectAnswer, applyPlayersSnapshot, etc.) sont accédés
  // via des refs pour éviter de fermer le socket pendant le handshake.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, wsUrl]);

  const handleSubmitAnswer = (value) => {
    const ws = wsRef.current;
    const trimmedValue = value.trim();

    if (!ws || ws.readyState !== WebSocket.OPEN) {
      setAnswerFeedback("Connexion au jeu pas encore prete. Reessaie dans un instant.");
      return false;
    }

    const entryId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    setAnswerFeedback("Réponse envoyee...");
    pendingAnswerIdsRef.current.push(entryId);
    setAnswerHistory((previousHistory) => [
      ...previousHistory,
      {
        id: entryId,
        text: trimmedValue,
        status: "pending",
      },
    ]);

    ws.send(
        JSON.stringify({
          type: "ANSWER_SUBMIT",
          answer: trimmedValue,
          player_id: authenticatedUserId || joinPlayerId,
          pseudo: user?.username || joinPseudo,
          avatar_id: joinAvatarId,
          guest_id: joinGuestId,
        })
      );

    return true;
  };

  const handleSendChatMessage = (message) => {
    const ws = wsRef.current;

    if (!isAuthenticated || !ws || ws.readyState !== WebSocket.OPEN) {
      return false;
    }

    setChatFeedback("");
    ws.send(
      JSON.stringify({
        type: "CHAT_SEND",
        message: message.trim(),
      })
    );
    return true;
  };

  return (
    <div
      className="mx-auto max-w-[1600px] px-3 py-3 sm:px-5 sm:py-4 lg:px-6 lg:py-4"
      onClickCapture={handleRoomLinkClick}
    >
      <RoomBattleBanner categorySlug={slug} />
      <div className="mt-3">
      <GameRoomLayout
        category={category}
        slug={slug}
        phase={phase}
        remaining={remaining}
        roundIndex={roundIndex}
        totalRounds={totalRounds}
        rounds={rounds}
        imageUrl={currentImageUrl}
        submittedBy={currentImageSubmittedBy}
        sourceType={currentImageSourceType}
        correctAnswer={currentCorrectAnswer}
        debugAnswer={debugAnswer}
        onSubmitAnswer={handleSubmitAnswer}
        answerDisabled={
          !isSocketReady || phase === "break" || answerStatus === "correct"
        }
        lastAnswer={lastAnswer}
        answerStatus={answerStatus}
        partialMatchedWords={partialMatchedWords}
        answerHistory={answerHistory}
        roundHistory={roundHistory}
        correctStreak={correctStreak}
        fastStreak={fastStreak}
        wrongStreak={wrongStreak}
        players={players}
        currentPlayerUid={currentPlayerUid}
        isAuthenticated={isAuthenticated}
        gamesCompletedTick={gamesCompletedTick}
        canModeratePlayers={canModeratePlayers}
        flawlessBonusUids={flawlessBonusUids}
        chatMessages={chatMessages}
        chatFeedback={chatFeedback}
        onSendChatMessage={handleSendChatMessage}
        chatDisabled={!isSocketReady}
        onPlayAnotherCategory={handlePlayAnotherCategory}
      />
      </div>

      <BugReportWidget
        categorySlug={slug}
        pseudo={joinPseudo || ""}
      />

      <GameResultOverlay
        isOpen={showResultOverlay}
        onClose={() => {
          setShowResultOverlay(false);
          setPostGameBreakRemaining(null);
          setIsTrackingPostGameInterCycle(false);
          isTrackingPostGameInterCycleRef.current = false;
        }}
        results={gameResults || []}
        currentPlayer={user}
        currentPlayerUid={currentPlayerUid}
        breakRemaining={postGameBreakRemaining}
        categoryName={category?.name || slug}
        currentCategorySlug={slug}
        onPlayAnotherCategory={handlePlayAnotherCategory}
        onConfirmLeavingRoom={confirmLeavingRoom}
      />
    </div>
  );
}
